import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ShieldCheckIcon, ClockIcon, ExclamationTriangleIcon, KeyIcon, ChevronDownIcon, ChevronUpIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { base64urlToUint8Array, uint8ArrayToBase64url } from '../../utils/webauthn';
import { usePasskeyChallenge } from '../../hooks/usePasskeyChallenge';

interface PasskeyConfig {
  login: boolean;
  security_settings: boolean;
  delete_account: boolean;
  end_session: boolean;
  end_all_sessions: boolean;
  revoke_device: boolean;
  create_cli_token: boolean;
  access_blocking: boolean;
  new_folder_creation: boolean;
}

interface UserPreferences {
  id: string;
  user_id: string;
  session_timeout_value: number;
  session_timeout_unit: 'minutes' | 'hours' | 'days';
  require_2fa: boolean;
  enable_passkey: boolean;
  passkey_config?: PasskeyConfig;
  backup_master_keys: boolean;
  allow_multi_device_access: boolean;
  created_at: string;
  updated_at: string;
}

interface WebAuthnCredential {
  id: string;
  aaguid: string;
  attestation_type: string;
  transports: string[];
  created_at: string;
  last_used_at?: string;
}

const SecurityTab: React.FC = () => {
  const { t } = useTranslation('settings');
  const { logout, user } = useAuth();
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Company security settings state (for admins only)
  const [companySecuritySettings, setCompanySecuritySettings] = useState<{
    session_timeout_value?: number;
    session_timeout_unit?: 'minutes' | 'hours' | 'days';
  } | null>(null);
  
  // Session timeout form state
  const [timeoutValue, setTimeoutValue] = useState<number>(24);
  const [timeoutUnit, setTimeoutUnit] = useState<'minutes' | 'hours' | 'days'>('hours');
  const [timeoutError, setTimeoutError] = useState<string | null>(null);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingTimeoutValue, setPendingTimeoutValue] = useState<number>(24);
  const [pendingTimeoutUnit, setPendingTimeoutUnit] = useState<'minutes' | 'hours' | 'days'>('hours');
  
  // Tooltip state for limits
  const [showLimitsTooltip, setShowLimitsTooltip] = useState(false);
  
  // Passkey registration state
  const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false);
  const [passkeyCredentials, setPasskeyCredentials] = useState<WebAuthnCredential[]>([]);
  const [showPasskeyConfig, setShowPasskeyConfig] = useState(false);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [showDeletePasskeyModal, setShowDeletePasskeyModal] = useState(false);
  const [passkeyToDelete, setPasskeyToDelete] = useState<string | null>(null);
  
  // Security settings lock state (for passkey-protected editing)
  const [isSecuritySettingsUnlocked, setIsSecuritySettingsUnlocked] = useState(false);
  const securitySettingsChallenge = usePasskeyChallenge('security_settings');
  
  // Update hook's user preferences when they change
  useEffect(() => {
    if (userPreferences) {
      // Force the hook to re-check by triggering a preference reload
      // The hook will check preferences on each executeWithChallenge call
    }
  }, [userPreferences]);

  // Check if user is company admin
  const isCompanyAdmin = user?.role === 'admin' && user?.company_id;

  useEffect(() => {
    loadPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPasskeyCredentials = useCallback(async () => {
    try {
      const response = await apiService.webauthnListCredentials();
      setPasskeyCredentials(response.credentials || []);
    } catch (err) {
      console.error('Failed to load passkey credentials:', err);
      // Don't show error if user doesn't have any credentials yet
    }
  }, []);

  useEffect(() => {
    if (userPreferences?.enable_passkey) {
      loadPasskeyCredentials();
    }
  }, [userPreferences?.enable_passkey, loadPasskeyCredentials]);

  // Reset unlock state when security_settings requirement changes
  useEffect(() => {
    if (!userPreferences?.enable_passkey || !userPreferences?.passkey_config?.security_settings) {
      setIsSecuritySettingsUnlocked(false);
    }
  }, [userPreferences?.enable_passkey, userPreferences?.passkey_config?.security_settings]);

  // Check if security settings require passkey
  const isSecuritySettingsLocked = userPreferences?.enable_passkey && 
                                   userPreferences?.passkey_config?.security_settings === true &&
                                   !isSecuritySettingsUnlocked;

  // Handle unlock security settings
  const handleUnlockSecuritySettings = async () => {
    const success = await securitySettingsChallenge.executeWithChallenge(async () => {
      setIsSecuritySettingsUnlocked(true);
      toast.success(t('security.unlocked'));
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  const loadCompanySecuritySettings = useCallback(async () => {
    if (!user?.company_id) return;
    
    try {
      const settings = await apiService.getCompanySecuritySettings(user.company_id);
      setCompanySecuritySettings(settings);
      
      // Set form state from company settings
      if (settings.session_timeout_value && settings.session_timeout_unit) {
        setTimeoutValue(settings.session_timeout_value);
        setTimeoutUnit(settings.session_timeout_unit);
      } else {
        // Default to 24 hours if not set
        setTimeoutValue(24);
        setTimeoutUnit('hours');
      }
    } catch (err) {
      console.error('Failed to load company security settings:', err);
      // Don't show error toast - just use defaults
      setTimeoutValue(24);
      setTimeoutUnit('hours');
    }
  }, [user?.company_id]);

  useEffect(() => {
    if (isCompanyAdmin && user?.company_id) {
      loadCompanySecuritySettings();
    }
  }, [isCompanyAdmin, user?.company_id, loadCompanySecuritySettings]);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showLimitsTooltip) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.limits-tooltip-container')) {
        setShowLimitsTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLimitsTooltip]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getUserPreferences();
      const prefs = response.preferences || response;
      setUserPreferences(prefs);
    } catch (err) {
      console.error('Failed to load security preferences:', err);
      setError(t('security.loadFailed'));
      toast.error(t('security.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const validateSessionTimeout = (value: number, unit: 'minutes' | 'hours' | 'days'): string | null => {
    if (value <= 0) {
      return t('security.sessionTimeoutGreaterThanZero');
    }
    
    if (unit === 'minutes') {
      if (value < 5) {
        return t('security.sessionTimeoutMin5Minutes');
      }
      if (value > 7 * 24 * 60) { // 7 days in minutes
        return t('security.sessionTimeoutMax7DaysMinutes');
      }
    } else if (unit === 'hours') {
      if (value < 1) {
        return t('security.sessionTimeoutMin1Hour');
      }
      if (value > 7 * 24) { // 7 days in hours
        return t('security.sessionTimeoutMax7DaysHours');
      }
    } else { // days
      if (value < 1) {
        return t('security.sessionTimeoutMin1Day');
      }
      if (value > 7) { // 7 days
        return t('security.sessionTimeoutMax7Days');
      }
    }
    
    return null;
  };

  const handleSessionTimeoutChange = (value: number, unit: 'minutes' | 'hours' | 'days') => {
    setTimeoutValue(value);
    setTimeoutUnit(unit);
    
    // Validate immediately
    const validationError = validateSessionTimeout(value, unit);
    setTimeoutError(validationError);
  };

  const handleSessionTimeoutSave = async () => {
    if (!isCompanyAdmin || !user?.company_id) return;
    
    // Validate before saving
    const validationError = validateSessionTimeout(timeoutValue, timeoutUnit);
    if (validationError) {
      setTimeoutError(validationError);
      toast.error(validationError);
      return;
    }
    
    // Check if values changed
    if (
      companySecuritySettings &&
      timeoutValue === companySecuritySettings.session_timeout_value &&
      timeoutUnit === companySecuritySettings.session_timeout_unit
    ) {
      // No changes to save
      return;
    }

    // Show confirmation modal
    setPendingTimeoutValue(timeoutValue);
    setPendingTimeoutUnit(timeoutUnit);
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async (activateNow: boolean) => {
    if (!isCompanyAdmin || !user?.company_id) return;

    try {
      setSaving(true);
      setError(null);
      setTimeoutError(null);
      setShowConfirmModal(false);

      // Update company security settings (applies to all users in the company)
      await apiService.updateCompanySecuritySettings(user.company_id, {
        session_timeout_value: pendingTimeoutValue,
        session_timeout_unit: pendingTimeoutUnit,
      }, activateNow);

      // Update local state
      setCompanySecuritySettings({
        session_timeout_value: pendingTimeoutValue,
        session_timeout_unit: pendingTimeoutUnit,
      });
      
      // Update form state to match saved values
      setTimeoutValue(pendingTimeoutValue);
      setTimeoutUnit(pendingTimeoutUnit);
      
      if (activateNow) {
        toast.success(t('security.sessionTimeoutUpdated'));
        // Wait a moment for the toast to show, then logout
        setTimeout(async () => {
          await logout();
        }, 1500);
      } else {
        toast.success(t('security.sessionTimeoutUpdatedLater'));
      }
    } catch (err: any) {
      console.error('Failed to update company session timeout:', err);
      const errorMessage = err.response?.data?.error || t('security.sessionTimeoutFailed');
      setError(errorMessage);
      setTimeoutError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (field: keyof UserPreferences, value: boolean) => {
    if (!userPreferences || saving) return;

    try {
      setSaving(true);
      setError(null);

      const updates: any = { [field]: value };
      
      // If enabling passkey, ensure passkey_config is set with default operations (all false)
      if (field === 'enable_passkey' && value && !userPreferences.passkey_config) {
        updates.passkey_config = {
          login: false,
          security_settings: false,
          delete_account: false,
          end_session: false,
          end_all_sessions: false,
          revoke_device: false,
          create_cli_token: false,
          access_blocking: false,
          new_folder_creation: false,
        };
      }

      await apiService.updateUserPreferences(updates);

      const updatedPrefs = {
        ...userPreferences,
        [field]: value,
        ...(updates.passkey_config && { passkey_config: updates.passkey_config }),
      };
      setUserPreferences(updatedPrefs);
      
      if (field === 'enable_passkey' && value) {
        setShowPasskeyConfig(true);
        loadPasskeyCredentials();
      }
      
      toast.success(t('security.preferenceUpdated'));
    } catch (err: any) {
      console.error('Failed to update security preference:', err);
      const errorMessage = err.response?.data?.error || t('security.preferenceFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterPasskey = async () => {
    if (!user || isRegisteringPasskey) return;

    try {
      setIsRegisteringPasskey(true);
      
      // Get user info for registration
      const beginResponse = await apiService.webauthnBeginRegistration(
        user.id,
        user.email,
        user.name || user.email,
        user.name || user.email
      );

      const { options, session_id } = beginResponse;

      // Use browser WebAuthn API
      const challengeBytes = base64urlToUint8Array(options.publicKey.challenge) as unknown as Uint8Array;
      const userIdBytes = base64urlToUint8Array(options.publicKey.user.id) as unknown as Uint8Array;
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: challengeBytes.buffer as ArrayBuffer,
          rp: options.publicKey.rp,
          user: {
            id: userIdBytes.buffer as ArrayBuffer,
            name: options.publicKey.user.name,
            displayName: options.publicKey.user.displayName,
          },
          pubKeyCredParams: options.publicKey.pubKeyCredParams,
          authenticatorSelection: options.publicKey.authenticatorSelection,
          timeout: options.publicKey.timeout || 60000,
          attestation: options.publicKey.attestation || 'none',
        },
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('User cancelled passkey registration');
      }

      // Convert response to WebAuthn JSON format (base64url-encoded strings)
      const response = credential.response as AuthenticatorAttestationResponse;
      const registrationResponse = {
        id: credential.id,
        rawId: uint8ArrayToBase64url(new Uint8Array(credential.rawId)),
        response: {
          attestationObject: uint8ArrayToBase64url(new Uint8Array(response.attestationObject)),
          clientDataJSON: uint8ArrayToBase64url(new Uint8Array(response.clientDataJSON)),
        },
        type: credential.type,
      };

      // Finish registration
      await apiService.webauthnFinishRegistration(session_id, registrationResponse);

      toast.success(t('security.passkeyRegistered'));
      await loadPasskeyCredentials();
      setShowPasskeyModal(false); // Close modal after registration
    } catch (error: any) {
      console.error('Passkey registration error:', error);
      let errorMessage = t('security.passkeyRegistrationFailed');
      if (error.name === 'NotAllowedError') {
        errorMessage = t('security.passkeyCancelled');
      } else if (error.name === 'InvalidStateError') {
        errorMessage = t('security.passkeyAlreadyRegistered');
      } else if (error.name === 'NotSupportedError') {
        errorMessage = t('security.passkeyNotSupported');
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsRegisteringPasskey(false);
    }
  };

  const handleDeletePasskeyClick = (credentialId: string) => {
    setPasskeyToDelete(credentialId);
    setShowDeletePasskeyModal(true);
  };

  const confirmDeletePasskey = async () => {
    if (!passkeyToDelete) return;

    try {
      await apiService.webauthnDeleteCredential(passkeyToDelete);
      toast.success(t('security.passkeyDeleted'));
      await loadPasskeyCredentials();
      setShowDeletePasskeyModal(false);
      setPasskeyToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete passkey:', error);
      toast.error(error.response?.data?.error || t('security.passkeyDeleteFailed'));
    }
  };

  const cancelDeletePasskey = () => {
    setShowDeletePasskeyModal(false);
    setPasskeyToDelete(null);
  };

  const handlePasskeyConfigChange = async (operation: keyof PasskeyConfig, value: boolean) => {
    if (!userPreferences || saving) return;

    try {
      setSaving(true);
      const currentConfig = userPreferences.passkey_config || {
        login: false,
        security_settings: false,
        delete_account: false,
        end_session: false,
        end_all_sessions: false,
        revoke_device: false,
        create_cli_token: false,
        access_blocking: false,
        new_folder_creation: false,
      };

      const updatedConfig = {
        ...currentConfig,
        [operation]: value,
      };

      await apiService.updateUserPreferences({
        passkey_config: updatedConfig,
      });

      setUserPreferences({
        ...userPreferences,
        passkey_config: updatedConfig,
      });

      toast.success(t('security.passkeyConfigUpdated'));
    } catch (error: any) {
      console.error('Failed to update passkey config:', error);
      toast.error(error.response?.data?.error || t('security.passkeyConfigFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Convert timeout to display format
  const getTimeoutDisplay = (value: number, unit: 'minutes' | 'hours' | 'days'): string => {
    if (unit === 'days') {
      return `${value} day${value !== 1 ? 's' : ''}`;
    } else if (unit === 'hours') {
      if (value === 1) return '1 hour';
      if (value < 24) return `${value} hours`;
      if (value === 24) return '1 day';
      const days = Math.floor(value / 24);
      const hours = value % 24;
      if (hours === 0) return `${days} day${days > 1 ? 's' : ''}`;
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`;
    } else { // minutes
      if (value < 60) return `${value} minute${value !== 1 ? 's' : ''}`;
      const hours = Math.floor(value / 60);
      const minutes = value % 60;
      if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
        <div className="px-4 py-5 sm:px-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userPreferences) {
    return (
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
        <div className="px-4 py-5 sm:px-6">
          <div className="text-red-600 dark:text-red-400">{error}</div>
          <button
            onClick={loadPreferences}
            className="mt-4 px-4 py-2 bg-governate-600 text-white rounded-md hover:bg-governate-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
              <ShieldCheckIcon className="h-5 w-5 mr-2" />
              {t('security.securityPreferences')}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              {t('security.description')}
            </p>
            {/* Unlock/Lock buttons - below description on mobile */}
            <div className="mt-3 md:hidden">
              {isSecuritySettingsLocked && (
                <button
                  onClick={handleUnlockSecuritySettings}
                  disabled={securitySettingsChallenge.isChallenging}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 border border-governate-300 dark:border-governate-600 rounded-md hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors disabled:opacity-50"
                >
                  {securitySettingsChallenge.isChallenging ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      <span>{t('security.unlocking')}</span>
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="h-4 w-4" />
                      <span>{t('security.unlockToEdit')}</span>
                    </>
                  )}
                </button>
              )}
              {!isSecuritySettingsLocked && userPreferences?.enable_passkey && userPreferences?.passkey_config?.security_settings && (
                <button
                  onClick={() => setIsSecuritySettingsUnlocked(false)}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-colors"
                >
                  <LockOpenIcon className="h-4 w-4" />
                  <span>{t('security.lock')}</span>
                </button>
              )}
            </div>
          </div>
          {/* Unlock/Lock buttons - right side on desktop */}
          <div className="hidden md:block">
            {isSecuritySettingsLocked && (
              <button
                onClick={handleUnlockSecuritySettings}
                disabled={securitySettingsChallenge.isChallenging}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 border border-governate-300 dark:border-governate-600 rounded-md hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors disabled:opacity-50"
              >
                {securitySettingsChallenge.isChallenging ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    <span>{t('security.unlocking')}</span>
                  </>
                ) : (
                  <>
                    <LockClosedIcon className="h-4 w-4" />
                    <span>{t('security.unlockToEdit')}</span>
                  </>
                )}
              </button>
            )}
            {!isSecuritySettingsLocked && userPreferences?.enable_passkey && userPreferences?.passkey_config?.security_settings && (
              <button
                onClick={() => setIsSecuritySettingsUnlocked(false)}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-colors"
              >
                <LockOpenIcon className="h-4 w-4" />
                <span>{t('security.lock')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-800">
        <div className="px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-800">
            {/* Session Timeout - Only show for company admins */}
            {isCompanyAdmin && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  {t('security.companySessionTimeout')}
                </dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-900 dark:text-white mb-2">
                        {t('security.howLongSessions')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        {t('security.noteSessionTimeout')}
                      </p>
                    </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <input
                        type="number"
                        min={timeoutUnit === 'minutes' ? 5 : timeoutUnit === 'hours' ? 1 : 1}
                        max={timeoutUnit === 'minutes' ? 10080 : timeoutUnit === 'hours' ? 168 : 7}
                        value={timeoutValue}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || 0;
                          handleSessionTimeoutChange(value, timeoutUnit);
                        }}
                        disabled={isSecuritySettingsLocked}
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 ${
                          timeoutError
                            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                            : isSecuritySettingsLocked
                            ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50 cursor-not-allowed'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                        }`}
                        placeholder={timeoutUnit === 'minutes' ? '5-10080' : timeoutUnit === 'hours' ? '1-168' : '1-7'}
                      />
                    </div>
                    
                    <div className="w-32">
                      <select
                        value={timeoutUnit}
                        onChange={(e) => {
                          const unit = e.target.value as 'minutes' | 'hours' | 'days';
                          handleSessionTimeoutChange(timeoutValue, unit);
                        }}
                        disabled={isSecuritySettingsLocked}
                        className={`w-full px-3 py-2 border rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 ${
                          isSecuritySettingsLocked
                            ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50 cursor-not-allowed'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                        }`}
                      >
                        <option value="minutes">{t('security.minutes')}</option>
                        <option value="hours">{t('security.hours')}</option>
                        <option value="days">{t('security.days')}</option>
                      </select>
                    </div>
                    
                    <button
                      onClick={handleSessionTimeoutSave}
                      disabled={isSecuritySettingsLocked || saving || !!timeoutError || (
                        companySecuritySettings !== null &&
                        timeoutValue === companySecuritySettings.session_timeout_value &&
                        timeoutUnit === companySecuritySettings.session_timeout_unit
                      )}
                      className="px-4 py-2 bg-governate-600 text-white text-sm font-medium rounded-md hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? t('security.saving') : t('security.save')}
                    </button>
                  </div>
                  
                  {timeoutError && (
                    <p className="text-xs text-red-600 dark:text-red-400">{timeoutError}</p>
                  )}
                  
                  {!timeoutError && timeoutValue > 0 && (
                    <div className="relative limits-tooltip-container">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('security.currentSetting', { value: getTimeoutDisplay(timeoutValue, timeoutUnit) })}
                        {companySecuritySettings &&
                         timeoutValue === companySecuritySettings.session_timeout_value &&
                         timeoutUnit === companySecuritySettings.session_timeout_unit && (
                          <span className="ml-2 text-green-600 dark:text-green-400">{t('security.saved')}</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowLimitsTooltip(!showLimitsTooltip)}
                          className="ml-2 text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 underline focus:outline-none focus:ring-2 focus:ring-governate-500 rounded"
                        >
                          {t('security.showLimits')}
                        </button>
                      </p>
                      {showLimitsTooltip && (
                        <div className="absolute left-0 top-6 z-10 w-64 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg text-xs text-gray-700 dark:text-gray-300">
                          <p className="font-medium mb-2 text-gray-900 dark:text-white">{t('security.limits')}</p>
                          <ul className="space-y-1">
                            <li>{t('security.minLimit')}</li>
                            <li>{t('security.maxLimit')}</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </dd>
            </div>
            )}

            {/* Require 2FA */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('security.require2fa')}
              </dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {t('security.enable2fa')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('security.require2faDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle('require_2fa', !userPreferences?.require_2fa)}
                    disabled={isSecuritySettingsLocked || saving}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                      userPreferences?.require_2fa
                        ? 'bg-governate-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } ${isSecuritySettingsLocked || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={userPreferences?.require_2fa}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        userPreferences?.require_2fa ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </dd>
            </div>

            {/* Enable Passkey */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-t border-gray-200 dark:border-gray-800">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                <KeyIcon className="h-5 w-5 mr-2" />
                {t('security.enablePasskey')}
              </dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {t('security.enablePasskeyAuth')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('security.passkeyDescription')}
                    </p>
                    {/* Manage button and toggle - below description on mobile */}
                    <div className="flex items-center gap-2 mt-3 md:hidden">
                      {userPreferences?.enable_passkey && passkeyCredentials.length > 0 && (
                        <button
                          onClick={() => setShowPasskeyModal(true)}
                          className="px-3 py-1.5 text-sm font-medium text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 border border-governate-300 dark:border-governate-600 rounded-md hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors"
                        >
                          {t('security.manage')}
                        </button>
                      )}
                      {userPreferences?.enable_passkey && passkeyCredentials.length === 0 && (
                        <button
                          onClick={handleRegisterPasskey}
                          disabled={isRegisteringPasskey}
                          className="px-3 py-1.5 text-sm font-medium text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 border border-governate-300 dark:border-governate-600 rounded-md hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors disabled:opacity-50"
                        >
                          {isRegisteringPasskey ? '...' : '+'}
                        </button>
                      )}
                      <button
                        onClick={() => handleToggle('enable_passkey', !userPreferences?.enable_passkey)}
                        disabled={isSecuritySettingsLocked || saving}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                          userPreferences?.enable_passkey
                            ? 'bg-governate-600'
                            : 'bg-gray-200 dark:bg-gray-700'
                        } ${isSecuritySettingsLocked || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        role="switch"
                        aria-checked={userPreferences?.enable_passkey}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            userPreferences?.enable_passkey ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  {/* Manage button and toggle - right side on desktop */}
                  <div className="flex items-center gap-2 hidden md:flex">
                    {userPreferences?.enable_passkey && passkeyCredentials.length > 0 && (
                      <button
                        onClick={() => setShowPasskeyModal(true)}
                        className="px-3 py-1.5 text-sm font-medium text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 border border-governate-300 dark:border-governate-600 rounded-md hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors"
                      >
                        {t('security.manage')}
                      </button>
                    )}
                    {userPreferences?.enable_passkey && passkeyCredentials.length === 0 && (
                      <button
                        onClick={handleRegisterPasskey}
                        disabled={isRegisteringPasskey}
                        className="px-3 py-1.5 text-sm font-medium text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 border border-governate-300 dark:border-governate-600 rounded-md hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors disabled:opacity-50"
                      >
                        {isRegisteringPasskey ? '...' : '+'}
                      </button>
                    )}
                    <button
                      onClick={() => handleToggle('enable_passkey', !userPreferences?.enable_passkey)}
                      disabled={isSecuritySettingsLocked || saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                        userPreferences?.enable_passkey
                          ? 'bg-governate-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      } ${isSecuritySettingsLocked || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={userPreferences?.enable_passkey}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          userPreferences?.enable_passkey ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Accordion for passkey operations configuration */}
                {userPreferences?.enable_passkey && (
                  <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setShowPasskeyConfig(!showPasskeyConfig)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('security.passkeyOperations')}
                      </span>
                      {showPasskeyConfig ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </button>

                    {showPasskeyConfig && (
                      <div className="px-4 py-4 space-y-3 bg-white dark:bg-gray-900">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          {t('security.selectOperationsRequirePasskey')}
                        </p>
                        
                        {/* Login */}
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-900 dark:text-white">{t('security.login')}</span>
                          <button
                            onClick={() => handlePasskeyConfigChange('login', !userPreferences?.passkey_config?.login)}
                            disabled={saving}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                              userPreferences?.passkey_config?.login
                                ? 'bg-governate-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            role="switch"
                            aria-checked={userPreferences?.passkey_config?.login}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                userPreferences?.passkey_config?.login ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {/* Security Settings */}
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-900 dark:text-white">{t('security.securitySettingsChanges')}</span>
                          <button
                            onClick={() => handlePasskeyConfigChange('security_settings', !userPreferences?.passkey_config?.security_settings)}
                            disabled={saving}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                              userPreferences?.passkey_config?.security_settings
                                ? 'bg-governate-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            role="switch"
                            aria-checked={userPreferences?.passkey_config?.security_settings}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                userPreferences?.passkey_config?.security_settings ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {/* Delete Account */}
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-900 dark:text-white">{t('security.deleteAccount')}</span>
                          <button
                            onClick={() => handlePasskeyConfigChange('delete_account', !userPreferences?.passkey_config?.delete_account)}
                            disabled={saving}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                              userPreferences?.passkey_config?.delete_account
                                ? 'bg-governate-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            role="switch"
                            aria-checked={userPreferences?.passkey_config?.delete_account}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                userPreferences?.passkey_config?.delete_account ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {/* End Session */}
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-900 dark:text-white">{t('security.endSingleSession')}</span>
                          <button
                            onClick={() => handlePasskeyConfigChange('end_session', !userPreferences?.passkey_config?.end_session)}
                            disabled={saving}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                              userPreferences?.passkey_config?.end_session
                                ? 'bg-governate-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            role="switch"
                            aria-checked={userPreferences?.passkey_config?.end_session}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                userPreferences?.passkey_config?.end_session ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {/* End All Sessions */}
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-900 dark:text-white">{t('security.endAllSessions')}</span>
                          <button
                            onClick={() => handlePasskeyConfigChange('end_all_sessions', !userPreferences?.passkey_config?.end_all_sessions)}
                            disabled={saving}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                              userPreferences?.passkey_config?.end_all_sessions
                                ? 'bg-governate-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            role="switch"
                            aria-checked={userPreferences?.passkey_config?.end_all_sessions}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                userPreferences?.passkey_config?.end_all_sessions ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {/* Revoke Device */}
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-900 dark:text-white">{t('security.revokeDevice')}</span>
                          <button
                            onClick={() => handlePasskeyConfigChange('revoke_device', !userPreferences?.passkey_config?.revoke_device)}
                            disabled={saving}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                              userPreferences?.passkey_config?.revoke_device
                                ? 'bg-governate-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            role="switch"
                            aria-checked={userPreferences?.passkey_config?.revoke_device}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                userPreferences?.passkey_config?.revoke_device ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {/* Create CLI Token */}
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-gray-900 dark:text-white">{t('security.createCliToken')}</span>
                          <button
                            onClick={() => handlePasskeyConfigChange('create_cli_token', !userPreferences?.passkey_config?.create_cli_token)}
                            disabled={saving}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                              userPreferences?.passkey_config?.create_cli_token
                                ? 'bg-governate-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            role="switch"
                            aria-checked={userPreferences?.passkey_config?.create_cli_token}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                userPreferences?.passkey_config?.create_cli_token ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {/* Access Blocking (Future) */}
                        <label className="flex items-center justify-between cursor-pointer opacity-50">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Access Blocking (Coming Soon)</span>
                          <button
                            disabled
                            className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700"
                            role="switch"
                            aria-checked={false}
                          >
                            <span className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 translate-x-0" />
                          </button>
                        </label>

                        {/* New Folder Creation (Future) */}
                        <label className="flex items-center justify-between cursor-pointer opacity-50">
                          <span className="text-sm text-gray-500 dark:text-gray-400">New Folder Creation (Coming Soon)</span>
                          <button
                            disabled
                            className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700"
                            role="switch"
                            aria-checked={false}
                          >
                            <span className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 translate-x-0" />
                          </button>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </dd>
            </div>

            {/* Backup Master Keys */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Backup Master Keys
              </dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>

                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Enable automatic backup of your master encryption keys for recovery purposes
                      </p>
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">(Coming soon)</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggle('backup_master_keys', !userPreferences?.backup_master_keys)}
                    disabled={true}
                    className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 opacity-50"
                    role="switch"
                    aria-checked={false}
                  >
                    <span className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-0" />
                  </button>
                </div>
              </dd>
            </div>

            {/* Allow Multi-Device Access */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Allow Multi-Device Access
              </dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
               
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Enable access to your account from multiple devices simultaneously
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle('allow_multi_device_access', !userPreferences?.allow_multi_device_access)}
                    disabled={isSecuritySettingsLocked || saving}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                      userPreferences?.allow_multi_device_access
                        ? 'bg-governate-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } ${isSecuritySettingsLocked || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={userPreferences?.allow_multi_device_access}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        userPreferences?.allow_multi_device_access ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
            
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('security.updateSessionTimeout')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('security.yourCompanySessionTimeoutWillBeChanged', { value: getTimeoutDisplay(pendingTimeoutValue, pendingTimeoutUnit) })}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {t('security.thisWillApplyToAllUsers')}
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-1">
                  ⚠️ {t('security.activateNowWarning')}
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  {t('security.activateNowWarningDesc')}
                </p>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('security.activateLaterDesc')}
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={saving}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {t('security.cancel')}
              </button>
              <button
                onClick={() => handleConfirmSave(false)}
                disabled={saving}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border-2 border-governate-500 dark:border-governate-600 text-governate-600 dark:text-governate-400 bg-white dark:bg-gray-800 hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors disabled:opacity-50"
              >
                {saving ? t('security.saving') : t('security.activateLater')}
              </button>
              <button
                onClick={() => handleConfirmSave(true)}
                disabled={saving}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-governate-600 text-white hover:bg-governate-700 transition-colors disabled:opacity-50"
              >
                {saving ? t('security.saving') : t('security.activateNow')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Passkey Management Modal */}
      {showPasskeyModal && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => setShowPasskeyModal(false)} />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
                    <KeyIcon className="h-5 w-5 mr-2" />
                    {t('security.managePasskeys')}
                  </h3>
                  <button
                    onClick={() => setShowPasskeyModal(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Registered Passkeys List */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                      {t('security.registeredPasskeys', { count: passkeyCredentials.length })}
                    </h4>
                    {passkeyCredentials.length > 0 ? (
                      <div className="space-y-3">
                        {passkeyCredentials.map((cred, idx) => {
                          const createdDate = new Date(cred.created_at);
                          const lastUsedDate = cred.last_used_at ? new Date(cred.last_used_at) : null;
                          
                          return (
                            <div
                              key={cred.id}
                              className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <KeyIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {t('security.passkeyNumber', { number: idx + 1 })}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                    {cred.transports && cred.transports.length > 0 && (
                                      <div>Transports: {cred.transports.join(', ')}</div>
                                    )}
                                    {cred.attestation_type && (
                                      <div>Type: {cred.attestation_type}</div>
                                    )}
                                    <div>Created: {createdDate.toLocaleDateString()} {createdDate.toLocaleTimeString()}</div>
                                    {lastUsedDate && (
                                      <div>Last used: {lastUsedDate.toLocaleDateString()} {lastUsedDate.toLocaleTimeString()}</div>
                                    )}
                                    {!lastUsedDate && (
                                      <div className="text-gray-400">Never used</div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeletePasskeyClick(cred.id)}
                                  className="ml-4 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                >
                                  {t('security.deletePasskey')}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('security.noPasskeysRegistered')}
                      </p>
                    )}
                  </div>

                  {/* Register New Passkey Button */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleRegisterPasskey}
                      disabled={isRegisteringPasskey}
                      className="w-full px-4 py-2 bg-governate-600 text-white text-sm font-medium rounded-md hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    >
                      {isRegisteringPasskey ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {t('security.registering')}
                        </>
                      ) : (
                        <>
                          <KeyIcon className="h-4 w-4 mr-2" />
                          {t('security.registerNewPasskey')}
                        </>
                      )}
                    </button>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                      {t('security.browserWillPrompt')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Passkey Confirmation Modal */}
      {showDeletePasskeyModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
            
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('security.deletePasskey')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('security.deletePasskeyConfirm')}
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelDeletePasskey}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('security.cancel')}
              </button>
              <button
                onClick={confirmDeletePasskey}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                {t('security.deletePasskey')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SecurityTab;

